import { prop } from 'ramda';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useGetCMSResourceURL } from 'js/hooks/useGetCMSResourceURL';
import { useAppSelector, useDeviceSelector, useGameConfigurationSelector } from 'js/store/hooks';
import { workerService } from 'js/services/worker.service';
import { TDeviceFormFactor } from 'js/hooks/device/store/types';

import { IGamesFeed } from '../../types';
import { addGamesFeed } from '../../store/actions';

const REQUESTS: {
    [key: string]: Promise<IGamesFeed> | undefined;
} = {};

export const useGetGamesFeed = () => {
    const dispatch = useDispatch();
    const getCMSResourceURL = useGetCMSResourceURL();
    const formFactor = useDeviceSelector(prop('formFactor'));
    const requestStamp = useAppSelector(prop('requestStamp'));
    const excludedGameCodes = useGameConfigurationSelector(prop('excludedGameCodes'));

    return useCallback(async (feedName: string): Promise<IGamesFeed> => {
        if (REQUESTS[feedName]) {
            return await workerService
                .request<{
                    formFactor: TDeviceFormFactor;
                    gamesFeed: IGamesFeed;
                    excludedGameCodes: string[];
                }, IGamesFeed>('gamesFeedParse', {
                    gamesFeed: await REQUESTS[feedName],
                    formFactor,
                    excludedGameCodes
                });
        }

        const gamesFeedURL = getCMSResourceURL(`/initialResources/gamesFeeds/${encodeURIComponent(feedName)}`, { t: requestStamp });

        REQUESTS[feedName] = workerService
            .request<{
                formFactor: TDeviceFormFactor;
                gamesFeedURL: string;
                excludedGameCodes: string[];
            }, IGamesFeed>('gamesFeed', {
                gamesFeedURL,
                formFactor,
                excludedGameCodes
            })
            .then((response: IGamesFeed) => {
                dispatch(addGamesFeed(response));

                return response;
            })
            .catch(() => {
                REQUESTS[feedName] = undefined;

                throw new Error(`[Games] unable to get games feed "${gamesFeedURL}"`);
            });

        return REQUESTS[feedName] as Promise<IGamesFeed>;
    }, [
        dispatch,
        formFactor,
        getCMSResourceURL,
        requestStamp,
        excludedGameCodes
    ]);
};
